import React, { useEffect, useMemo, useState } from "react";
import SchoolIcon from "@mui/icons-material/School";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import BusinessIcon from "@mui/icons-material/Business";
import PublicIcon from "@mui/icons-material/Public";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import * as XLSX from "xlsx";
import {
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Divider,
} from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  PieChart,
  Pie,
  Legend,
  Cell,
  Label,
  LabelList,
} from "recharts";

/** ---------- helpers ---------- **/
const fmtNumber = (n) =>
  n === null || n === undefined ? "—" : Number(n).toLocaleString();

const safeArr = (v) => (Array.isArray(v) ? v : []);

const toQueryCsv = (arrOrStr) => {
  if (!arrOrStr) return "";
  if (Array.isArray(arrOrStr)) return arrOrStr.filter(Boolean).join(",");
  return String(arrOrStr).trim();
};

const fmtDate = (v) => (v ? String(v).slice(0, 10) : "");

const downloadXlsx = (rows, filename = "training_export.xlsx") => {
  if (!rows || rows.length === 0) return;

  // NOTE: field names below match [dbo].[BI_ExportTrainingDataset]'s current
  // SELECT aliases (updated 2026-09: original view-based procedure kept
  // as-is, plus trainingAreas, otherTrainingAreas added).
  const exportRows = rows.map((r) => ({
    "Employee ID": r.employeeId,
    "National ID": r.NationalID,
    "Names": r.Names,
    "Date of Birth": fmtDate(r.BirthDate),
    Gender: r.Gender,
    "Marital Status": r.MartialStatus,
    "Phone Number": r.phoneNumber,
    Email: r.email,
    "Line Ministry": r.LineMinistry,
    Employer: r.Employer,
    "Position Name": r.PositionName,
    Level: r.level,
    "Employment Category": r.employmentCategory,
    "Education Background": r.EducationBackground,
    "Title of Training": r.TitleOfTraining,
    "Training Areas": r.trainingAreas,
    "Other Training Areas": r.otherTrainingAreas,
    "Start Date Program": fmtDate(r.Start_Date_Program),
    "End Date Program": fmtDate(r.End_Date_Program),
    "Duration (Days)": r.Duration,
    "Short-term/Long-Term": r["Short-term/Long-Term"],
    "Departure Date": fmtDate(r.departureDate),
    "Returning Date": fmtDate(r.returningDate),
    "Country of Training": r.CountryOfTraining,
    "Town of Training": r.TownOfTraining,
    "University/Institution": r.Univerity_Institution,
    "Funds Provider": r.FundsProvider,
    "Fund Provider Name": r.FundProviderName,
    "Expected Training Award": r.expectedTrainingAward,
    "Initiated Date": fmtDate(r.InitiatedDate),
    "Submitted Date to Mifotra": fmtDate(r.SubmittedDateToMifotra),
    "Date Approved by Mifotra": fmtDate(r.DateApprovedByMifotra),
    "Status": r.StatusName,
  }));

  // Defensive check: if every value in the first row is empty, something
  // upstream changed shape (API field rename, stale bundle, etc.) - warn
  // loudly in the console instead of silently producing a blank workbook.
  const firstRowHasData = Object.values(exportRows[0] || {}).some(
    (v) => v !== undefined && v !== null && v !== ""
  );
  if (!firstRowHasData) {
    // eslint-disable-next-line no-console
    console.warn(
      "[downloadXlsx] First export row has no populated fields - check that the field names in downloadXlsx match the API response shape.",
      rows[0]
    );
  }

  const worksheet = XLSX.utils.json_to_sheet(exportRows);

  const headers = Object.keys(exportRows[0] || {});
  const narrowCols = new Set(["Employee ID", "Duration (Days)", "Gender", "Marital Status"]);
  const wideCols = new Set(["Names", "Position Name", "Title of Training", "Training Areas", "Other Training Areas", "University/Institution", "Fund Provider Name"]);
  worksheet["!cols"] = headers.map((h) => {
    if (narrowCols.has(h)) return { wch: 14 };
    if (wideCols.has(h)) return { wch: 32 };
    if (h.endsWith("Date")) return { wch: 20 };
    return { wch: 20 };
  });

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Training Data");

  XLSX.writeFile(workbook, filename);
};

const sumValues = (rows, key = "value") =>
  safeArr(rows).reduce((acc, cur) => acc + Number(cur?.[key] || 0), 0);

const PIE_COLORS = ["#1565c0", "#ef6c00", "#2e7d32", "#8e24aa", "#00838f", "#c62828"];

const renderPieOuterLabel = ({ percent }) =>
  `${((percent || 0) * 100).toFixed(0)}%`;

const MultiSelect = ({ label, value, options, onChange }) => {
  return (
    <FormControl fullWidth>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        renderValue={(selected) => (
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            {selected.map((v) => (
              <Chip key={v} label={v} size="small" />
            ))}
          </Box>
        )}
      >
        {options.map((o) => (
          <MenuItem key={o} value={o}>
            {o}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

 const StatCard = ({ label, value, icon: Icon, bgColor, iconColor }) => (
  <Card
    variant="outlined"
    sx={{
      height: "100%",
      borderRadius: 3,
      background: bgColor,
      borderColor: "rgba(0,0,0,0.08)",
      boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
    }}
  >
    <CardContent>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant="h4" fontWeight={800}>
            {value}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
        </Box>

        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255,255,255,0.7)",
          }}
        >
          {Icon ? <Icon sx={{ fontSize: 28, color: iconColor }} /> : null}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const PiePanel = ({ title, subtitle, data, totalLabel = "Total" }) => {
  const total = sumValues(data);

  return (
    <Card variant="outlined" sx={{ height: 340 }}>
      <CardContent>
        <Typography variant="h6" fontWeight={700}>
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        ) : null}

        <Box mt={1} sx={{ height: 245 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={safeArr(data)}
                dataKey="value"
                nameKey="name"
                outerRadius={90}
                innerRadius={35}
                labelLine={false}
                label={renderPieOuterLabel}
              >
                {safeArr(data).map((_, idx) => (
                  <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                ))}
                <Label
                  position="center"
                  content={({ viewBox }) => {
                    const { cx, cy } = viewBox || {};
                    return (
                      <g>
                        <text x={cx} y={cy - 2} textAnchor="middle" fontSize="18" fontWeight="700">
                          {fmtNumber(total)}
                        </text>
                        <text x={cx} y={cy + 16} textAnchor="middle" fontSize="11" fill="#666">
                          {totalLabel}
                        </text>
                      </g>
                    );
                  }}
                />
              </Pie>
              <Tooltip formatter={(value, name) => [fmtNumber(value), name]} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
};


 const CompactTable = ({ title, rows, col1, col2, col3 }) => {
  const hasThirdCol = !!col3;

  return (
    <Card variant="outlined" sx={{ height: "100%" }}>
      <CardContent sx={{ pb: "12px !important" }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          {title}
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: hasThirdCol ? "1fr 78px 78px" : "1fr 90px",
            gap: 0.75,
          }}
        >
          <Typography fontWeight={700} fontSize={13}>
            {col1}
          </Typography>
          <Typography fontWeight={700} fontSize={13} textAlign="right">
            {col2}
          </Typography>
          {hasThirdCol ? (
            <Typography fontWeight={700} fontSize={13} textAlign="right">
              {col3}
            </Typography>
          ) : null}

          <Divider sx={{ gridColumn: "1 / -1", my: 0.25 }} />

          {rows.map((r, i) => (
            <React.Fragment key={i}>
              <Typography
                variant="body2"
                sx={{
                  pr: 1,
                  fontSize: 12,
                  lineHeight: 1.25,
                  wordBreak: "break-word",
                }}
              >
                {r[col1]}
              </Typography>

              <Typography variant="body2" fontSize={12} textAlign="right">
                {fmtNumber(r[col2])}
              </Typography>

              {hasThirdCol ? (
                <Typography variant="body2" fontSize={12} textAlign="right">
                  {fmtNumber(r[col3])}
                </Typography>
              ) : null}
            </React.Fragment>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

/** ---------- main ---------- **/
export default function TrainingData() {
  const today = new Date();
  const defaultTo = today.toISOString().slice(0, 10);
  const defaultFrom = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 6);
    return d.toISOString().slice(0, 10);
  }, []);

  const [filtersOpen, setFiltersOpen] = useState(true);

  const [dateFrom, setDateFrom] = useState(defaultFrom);
  const [dateTo, setDateTo] = useState(defaultTo);

  const [providers, setProviders] = useState([]);
  const [levels, setLevels] = useState([]);
  const [countries, setCountries] = useState([]);
  const [trainingAreas, setTrainingAreas] = useState([]);
  const [trainingCategories, setTrainingCategories] = useState([]);

  const [topN, setTopN] = useState(10);

  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");

  const [data, setData] = useState({
    kpis: null,
    genderPies: [],
    trend: [],
    byCategory: [],
    byCountry: [],
    byArea: [],
    topCountries: [],
    topInstitutions: [],
    topFunders: [],
    topProviderTypes: [],
    lists: { levels: [], countries: [], areas: [], categories: [], providers: [] },
  });

  const buildDashboardUrl = (override = {}) => {
    const params = new URLSearchParams();

    const df = override.dateFrom ?? dateFrom;
    const dt = override.dateTo ?? dateTo;
    const tp = override.topN ?? topN;

    if (df) params.set("dateFrom", df);
    if (dt) params.set("dateTo", dt);
    if (tp) params.set("topN", String(tp));

    const p = toQueryCsv(override.providers ?? providers);
    const lv = toQueryCsv(override.levels ?? levels);
    const c = toQueryCsv(override.countries ?? countries);
    const a = toQueryCsv(override.trainingAreas ?? trainingAreas);
    const cat = toQueryCsv(override.trainingCategories ?? trainingCategories);

    if (p) params.set("providers", p);
    if (lv) params.set("levels", lv);
    if (c) params.set("countries", c);
    if (a) params.set("trainingAreas", a);
    if (cat) params.set("trainingCategories", cat);

    return `/api/bi/training-data/dashboard?${params.toString()}`;
  };

  const buildExportUrl = () => {
    const params = new URLSearchParams();
    if (dateFrom) params.set("dateFrom", dateFrom);
    if (dateTo) params.set("dateTo", dateTo);

    const p = toQueryCsv(providers);
    const lv = toQueryCsv(levels);
    const c = toQueryCsv(countries);
    const a = toQueryCsv(trainingAreas);
    const cat = toQueryCsv(trainingCategories);

    if (p) params.set("providers", p);
    if (lv) params.set("levels", lv);
    if (c) params.set("countries", c);
    if (a) params.set("trainingAreas", a);
    if (cat) params.set("trainingCategories", cat);

    return `/api/bi/training-data/export?${params.toString()}`;
  };

  const loadDashboard = async (override = {}) => {
    setLoading(true);
    setError("");
    try {
      const url = buildDashboardUrl(override);
      const res = await fetch(url);
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || "Dashboard API failed");
      }
      const json = await res.json();
      setData(json);
    } catch (e) {
      setError(e?.message || "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const exportDataset = async () => {
    setExporting(true);
    setError("");
    try {
      const url = buildExportUrl();
      const res = await fetch(url);
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || "Export API failed");
      }
      const json = await res.json();
      const rows = json?.rows || [];
      if (!rows.length) {
        throw new Error("No data to export for the selected filters.");
      }
       const fn = `training_export_${dateFrom}_to_${dateTo}.xlsx`;
       downloadXlsx(rows, fn);
    } catch (e) {
      setError(e?.message || "Failed to export dataset");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const listLevels = safeArr(data?.lists?.levels);
  const listCountries = safeArr(data?.lists?.countries);
  const listAreas = safeArr(data?.lists?.areas);
  const listCategories = safeArr(data?.lists?.categories);
  const listProviders = safeArr(data?.lists?.providers);

  const genderPieData = (grp) => {
    const rows = safeArr(data.genderPies).filter((x) => x.grp === grp);
    const male = rows.find((x) => String(x.genderId) === "1")?.trainings || 0;
    const female = rows.find((x) => String(x.genderId) === "2")?.trainings || 0;
    return [
      { name: "Male", value: Number(male) },
      { name: "Female", value: Number(female) },
    ];
  };

  const categoryPie = useMemo(() => {
    return safeArr(data.byCategory).map((x) => ({
      name: x.training_category,
      value: Number(x.TotalTrainings || 0),
    }));
  }, [data.byCategory]);

  const trendRows = useMemo(() => {
    const r = safeArr(data.trend)
      .slice()
      .sort((a, b) => String(a.YearMonth).localeCompare(String(b.YearMonth)));
    return r.map((x) => ({
      YearMonth: x.YearMonth,
      TotalTrainings: Number(x.TotalTrainings || 0),
      MaleTrainings: Number(x.MaleTrainings || 0),
      FemaleTrainings: Number(x.FemaleTrainings || 0),
      Individuals: Number(x.Individuals || 0),
    }));
  }, [data.trend]);

  const providerTypeChartRows = useMemo(() => {
    return safeArr(data.topProviderTypes).map((x) => ({
      provider: x.provider,
      TotalTrainings: Number(x.TotalTrainings || 0),
      value: Number(x.TotalTrainings || 0),
      name: x.provider,
    }));
  }, [data.topProviderTypes]);

  const usePieInsteadOfLine = trendRows.length <= 2;

  const trendPieRows = useMemo(() => {
    return trendRows.map((x) => ({
      name: x.YearMonth,
      value: Number(x.TotalTrainings || 0),
    }));
  }, [trendRows]);

  const k = data.kpis || {};

  return (
    <Box className="p-3">
      <Grid container alignItems="center" justifyContent="space-between" spacing={2}>
        <Grid item>
          <Typography variant="h5" fontWeight={800}>
            Training Data Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Default view: last 6 months. Apply filters for monthly reporting and export.
          </Typography>
        </Grid>
        <Grid item display="flex" gap={1}>
          <Button variant="outlined" onClick={loadDashboard} disabled={loading}>
            Refresh
          </Button>
           <Button variant="contained" onClick={exportDataset} disabled={exporting || loading}>
            {exporting ? "Exporting..." : "Export Excel"}
           </Button>
        </Grid>
      </Grid>

      {error ? (
        <Box mt={2}>
          <Alert severity="error">{error}</Alert>
        </Box>
      ) : null}

      {/* Filters */}
      <Box mt={2}>
        <Button size="small" onClick={() => setFiltersOpen((v) => !v)}>
          {filtersOpen ? "Hide Filters" : "Show Filters"}
        </Button>

        <Collapse in={filtersOpen}>
          <Card variant="outlined" sx={{ mt: 1 }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="From"
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="To"
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <MultiSelect
                    label="Provider Types"
                    value={providers}
                    options={listProviders}
                    onChange={setProviders}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Top N"
                    type="number"
                    value={topN}
                    onChange={(e) => setTopN(Number(e.target.value || 10))}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: 1, max: 50 }}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <MultiSelect label="Levels" value={levels} options={listLevels} onChange={setLevels} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <MultiSelect
                    label="Training Countries"
                    value={countries}
                    options={listCountries}
                    onChange={setCountries}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <MultiSelect
                    label="Training Areas"
                    value={trainingAreas}
                    options={listAreas}
                    onChange={setTrainingAreas}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <MultiSelect
                    label="Training Categories"
                    value={trainingCategories}
                    options={listCategories}
                    onChange={setTrainingCategories}
                  />
                </Grid>

                <Grid item xs={12} display="flex" gap={1}>
                  <Button variant="contained" onClick={() => loadDashboard()} disabled={loading}>
                    {loading ? "Loading..." : "Apply Filters"}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setDateFrom(defaultFrom);
                      setDateTo(defaultTo);
                      setProviders([]);
                      setLevels([]);
                      setCountries([]);
                      setTrainingAreas([]);
                      setTrainingCategories([]);
                      setTopN(10);

                      loadDashboard({
                        dateFrom: defaultFrom,
                        dateTo: defaultTo,
                        providers: [],
                        levels: [],
                        countries: [],
                        trainingAreas: [],
                        trainingCategories: [],
                        topN: 10,
                      });
                    }}
                  >
                    Reset
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Collapse>
      </Box>

      {/* Loading indicator */}
      {loading ? (
        <Box mt={3} display="flex" gap={1} alignItems="center">
          <CircularProgress size={18} />
          <Typography>Loading dashboard data...</Typography>
        </Box>
      ) : null}

      {/* KPI Cards */}
   
     <Box mt={3}>
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: {
        xs: "1fr",
        sm: "repeat(2, 1fr)",
        md: "repeat(3, 1fr)",
        lg: "repeat(5, 1fr)",
      },
      gap: 2,
    }}
  >
    <StatCard
      label="Total individuals trained"
      value={fmtNumber(k.total_individuals_trained)}
      icon={SchoolIcon}
      bgColor="linear-gradient(135deg, #e3f2fd 0%, #f8fbff 100%)"
      iconColor="#1565c0"
    />

    <StatCard
      label="Total trainings"
      value={fmtNumber(k.total_training_records)}
      icon={MenuBookIcon}
      bgColor="linear-gradient(135deg, #fff3e0 0%, #fffaf5 100%)"
      iconColor="#ef6c00"
    />

    <StatCard
      label="Institutions"
      value={fmtNumber(k.institutions)}
      icon={BusinessIcon}
      bgColor="linear-gradient(135deg, #e8f5e9 0%, #f8fff9 100%)"
      iconColor="#2e7d32"
    />

    <StatCard
      label="Destination countries"
      value={fmtNumber(k.destination_countries)}
      icon={PublicIcon}
      bgColor="linear-gradient(135deg, #ede7f6 0%, #faf8ff 100%)"
      iconColor="#6a1b9a"
    />

    <StatCard
      label="Distinct funders"
      value={fmtNumber(k.distinct_funders)}
      icon={AccountBalanceWalletIcon}
      bgColor="linear-gradient(135deg, #fce4ec 0%, #fff9fb 100%)"
      iconColor="#c2185b"
   />
  </Box>
</Box>

      {/* Row 1: 4 pie charts */}
      <Box mt={3}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <PiePanel
              title="Gender Split — TOTAL"
              subtitle="Trainings by gender"
              data={genderPieData("TOTAL")}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <PiePanel
              title="Permanent Staff"
              subtitle="Trainings by gender"
              data={genderPieData("PERMANENT")}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <PiePanel
              title="Contractual Staff"
              subtitle="Trainings by gender"
              data={genderPieData("CONTRACTUAL")}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <PiePanel
              title="Trainings by Category"
              subtitle="Short term vs Long term"
              data={categoryPie}
              totalLabel="Trainings"
            />
          </Grid>
        </Grid>
      </Box>

      {/* Row 2: Monthly trend + Provider types chart (line or pie if <=2 months) */}
      <Box mt={3}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: 380 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={700}>
                  Monthly Trend
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {usePieInsteadOfLine
                    ? "Selected period has 1–2 months, so it is shown as a pie."
                    : "Total trainings + gender split over time."}
                </Typography>

                <Box mt={2} sx={{ height: 280 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    {usePieInsteadOfLine ? (
                      <PieChart>
                        <Pie
                          data={trendPieRows}
                          dataKey="value"
                          nameKey="name"
                          outerRadius={100}
                          innerRadius={40}
                          labelLine={false}
                          label={renderPieOuterLabel}
                        >
                          {trendPieRows.map((_, idx) => (
                            <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                          ))}
                          <Label
                            position="center"
                            content={({ viewBox }) => {
                              const { cx, cy } = viewBox || {};
                              return (
                                <g>
                                  <text x={cx} y={cy - 2} textAnchor="middle" fontSize="18" fontWeight="700">
                                    {fmtNumber(sumValues(trendPieRows))}
                                  </text>
                                  <text x={cx} y={cy + 16} textAnchor="middle" fontSize="11" fill="#666">
                                    Trainings
                                  </text>
                                </g>
                              );
                            }}
                          />
                        </Pie>
                        <Tooltip formatter={(value, name) => [fmtNumber(value), name]} />
                        <Legend />
                      </PieChart>
                    ) : (
                      <LineChart data={trendRows} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="YearMonth" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="TotalTrainings"
                          stroke="#1565c0"
                          strokeWidth={3}
                          dot={{ r: 4 }}
                        >
                          <LabelList dataKey="TotalTrainings" position="top" fontSize={11} />
                        </Line>
                        <Line
                          type="monotone"
                          dataKey="MaleTrainings"
                          stroke="#2e7d32"
                          strokeWidth={2}
                          dot={{ r: 3 }}
                        />
                        <Line
                          type="monotone"
                          dataKey="FemaleTrainings"
                          stroke="#ef6c00"
                          strokeWidth={2}
                          dot={{ r: 3 }}
                        />
                      </LineChart>
                    )}
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: 380 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={700}>
                  Funder Types
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {usePieInsteadOfLine
                    ? "Shown as a pie for a compact view."
                    : "Top provider types by trainings."}
                </Typography>

                <Box mt={2} sx={{ height: 280 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    {usePieInsteadOfLine ? (
                      <PieChart>
                        <Pie
                          data={providerTypeChartRows}
                          dataKey="value"
                          nameKey="name"
                          outerRadius={100}
                          innerRadius={40}
                          labelLine={false}
                          label={renderPieOuterLabel}
                        >
                          {providerTypeChartRows.map((_, idx) => (
                            <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                          ))}
                          <Label
                            position="center"
                            content={({ viewBox }) => {
                              const { cx, cy } = viewBox || {};
                              return (
                                <g>
                                  <text x={cx} y={cy - 2} textAnchor="middle" fontSize="18" fontWeight="700">
                                    {fmtNumber(sumValues(providerTypeChartRows))}
                                  </text>
                                  <text x={cx} y={cy + 16} textAnchor="middle" fontSize="11" fill="#666">
                                    Trainings
                                  </text>
                                </g>
                              );
                            }}
                          />
                        </Pie>
                        <Tooltip formatter={(value, name) => [fmtNumber(value), name]} />
                        <Legend />
                      </PieChart>
                    ) : (
                      <LineChart
                        data={providerTypeChartRows}
                        margin={{ top: 10, right: 20, left: 0, bottom: 20 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="provider" interval={0} angle={-10} textAnchor="end" height={55} />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="TotalTrainings"
                          stroke="#8e24aa"
                          strokeWidth={3}
                          dot={{ r: 4 }}
                          name="Trainings"
                        >
                          <LabelList dataKey="TotalTrainings" position="top" fontSize={11} />
                        </Line>
                      </LineChart>
                    )}
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Row 3: compact top lists */}
      <Box mt={3}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <CompactTable
              title={`Top ${topN} Destination Countries`}
              rows={safeArr(data.topCountries).slice(0, topN)}
              col1="training_country"
              col2="TotalTrainings"
              />
          </Grid>

          <Grid item xs={12} md={4}>
          <CompactTable
            title={`Top ${topN} Institutions`}
            rows={safeArr(data.topInstitutions).slice(0, topN)}
            col1="employer_institution"
            col2="TotalTrainings"
          />
         </Grid>
          <Grid item xs={12} md={4}>
            <Card variant="outlined" sx={{ height: "100%" }}>
              <CardContent sx={{ pb: "12px !important" }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  Top Funders (Names)
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 78px", gap: 0.75 }}>
                  <Typography fontWeight={700} fontSize={13}>
                    Funder
                  </Typography>
                  <Typography fontWeight={700} fontSize={13} textAlign="right">
                    Trainings
                  </Typography>
                  <Divider sx={{ gridColumn: "1 / -1", my: 0.25 }} />
                  {safeArr(data.topFunders)
                    .slice(0, topN)
                    .map((r, i) => (
                      <React.Fragment key={i}>
                        <Typography
                          variant="body2"
                          sx={{ pr: 1, fontSize: 12, lineHeight: 1.25, wordBreak: "break-word" }}
                        >
                          {r.funder_name_display}
                        </Typography>
                        <Typography variant="body2" fontSize={12} textAlign="right">
                          {fmtNumber(r.TotalTrainings)}
                        </Typography>
                      </React.Fragment>
                    ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}